/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Stage Canvas — left pane of the Run Viewer.
 *
 * Renders 5 stage cards in vertical flow (horizontal on wide desktop)
 * showing the agent loop progressing live. Each card surfaces:
 *
 *   - stage id + name
 *   - archetype badge (retriever, domain_specialist, etc.)
 *   - persona name (when persona.loaded has fired)
 *   - LLM tier (modal / claude / failed) — hidden in demo mode
 *   - tools invoked, with status pill (running / completed / failed)
 *   - latency
 *   - state-based color: pending (gray), running (pulsing cyan),
 *     completed (green), failed (red), awaiting_confirmation (yellow)
 *
 * When a stage is awaiting_confirmation we surface inline Approve / Reject
 * buttons. Clicking them is a no-op until Slice 4 wires the ApprovalModal —
 * Slice 3 just records the click so the affordance is testable.
 */

import React from 'react';
import {
  displayPersonaName,
  displayLatency,
  displayLlmTier,
  isDemoMode,
} from '../../../utils/demoMode';

function statusClass(status) {
  const s = String(status || 'pending').toLowerCase();
  if (s === 'running') return 'wf-stage-card wf-stage-running';
  if (s === 'completed') return 'wf-stage-card wf-stage-completed';
  if (s === 'failed') return 'wf-stage-card wf-stage-failed';
  if (s === 'awaiting_confirmation') return 'wf-stage-card wf-stage-pending-confirm';
  return 'wf-stage-card wf-stage-pending';
}

function toolStatusIcon(status) {
  if (status === 'running') return <span className="wf-tool-spinner" aria-label="running" />;
  if (status === 'completed') return <span className="wf-tool-check">✓</span>;
  if (status === 'failed') return <span className="wf-tool-x">✕</span>;
  return <span className="wf-tool-dot">•</span>;
}

export default function StageCanvas({ stages, onApprovalClick }) {
  const demo = isDemoMode();

  if (!stages || stages.length === 0) {
    return (
      <div className="wf-stage-empty">
        Waiting for the first stage to begin…
      </div>
    );
  }

  return (
    <ol className="wf-stage-canvas">
      {stages.map((stage, idx) => {
        const status = stage.status || 'pending';
        const klass = statusClass(status);
        const personaLabel = displayPersonaName(stage.persona || stage.persona_id, demo);
        return (
          <li key={stage.id || idx} className={klass}>
            <header className="wf-stage-head">
              <span className="wf-stage-step">Stage {idx + 1}</span>
              <h3 className="wf-stage-name">{stage.name || stage.id}</h3>
              {stage.archetype && (
                <span className="wf-stage-archetype">{stage.archetype}</span>
              )}
              <span className={`wf-stage-state-pill wf-stage-state-${status}`}>
                {status.replace('_', ' ')}
              </span>
            </header>

            <div className="wf-stage-body">
              {stage.persona && (
                <div className="wf-stage-row">
                  <span className="wf-stage-label">Persona</span>
                  <span className="wf-stage-value">{personaLabel}</span>
                </div>
              )}
              {stage.llm_tier && !demo && (
                <div className="wf-stage-row">
                  <span className="wf-stage-label">LLM tier</span>
                  <span className="wf-stage-value">{displayLlmTier(stage.llm_tier, false)}</span>
                </div>
              )}
              {stage.llm_tier && demo && (
                <div className="wf-stage-row">
                  <span className="wf-stage-label">Engine</span>
                  <span className="wf-stage-value">{displayLlmTier(stage.llm_tier, true)}</span>
                </div>
              )}
              {(stage.tools || []).length > 0 && (
                <div className="wf-stage-row wf-stage-row-tools">
                  <span className="wf-stage-label">Tools</span>
                  <ul className="wf-stage-tools">
                    {stage.tools.map((t, ti) => (
                      <li key={`${t.name}-${ti}`} className={`wf-stage-tool wf-stage-tool-${t.status || 'pending'}`}>
                        {toolStatusIcon(t.status)}
                        <span className="wf-stage-tool-name">{t.name}</span>
                        {t.latency_ms != null && !demo && (
                          <span className="wf-stage-tool-lat">{t.latency_ms}ms</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {stage.latency_ms != null && (
                <div className="wf-stage-row">
                  <span className="wf-stage-label">Latency</span>
                  <span className="wf-stage-value">{displayLatency(stage.latency_ms, demo)}</span>
                </div>
              )}
              {stage.error && (
                <div className="wf-stage-error">
                  <strong>Failed:</strong> {stage.error}
                </div>
              )}
            </div>

            {status === 'awaiting_confirmation' && (
              <footer className="wf-stage-confirm-bar">
                <button
                  type="button"
                  className="wf-btn wf-btn-primary"
                  onClick={() => onApprovalClick?.({
                    stage_id: stage.id,
                    confirmation_id: stage.confirmation_id,
                  })}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="wf-btn wf-btn-danger"
                  onClick={() => onApprovalClick?.({
                    stage_id: stage.id,
                    confirmation_id: stage.confirmation_id,
                    intent: 'reject',
                  })}
                >
                  Reject
                </button>
              </footer>
            )}
          </li>
        );
      })}
    </ol>
  );
}
